/**
 * 一个字的**笔顺动画**（Hanzi Writer）。
 *
 * ## 它做什么
 *
 * 点预习卡上的某个字 → 这里把那个字按**笔顺**一笔一笔画出来，可以"再放一遍"。
 * 这正是用户要的那件事："孩子在听写之前，可以先预习看一下那个字怎么写"。
 *
 * ## 依赖与许可（引入依赖就必须写清楚）
 *
 *   · `hanzi-writer@3.7.3`（MIT）—— 逐笔动画、描红都靠它，浏览器端库，**不打 CDN**
 *     （`deploy/Caddyfile` 的 CSP 是 `script-src 'self'`，CDN 脚本会被直接拦掉）；
 *   · 笔画数据来自 `hanzi-writer-data`（Make Me a Hanzi 的图形数据，
 *     **Arphic Public License**），构建时拷进 `dist/strokes/`（见 `vite.config.ts`）。
 *
 * ## 数据**不是**"点开才从网上找"的
 *
 * `lib/hanziStrokes.ts` 负责取数与判形状（含那个"404 会变成 200 + index.html"
 * 的生产坑）。这里只负责：取不到时说人话、取到了就画。
 *
 * ## 拿不到数据时**绝不放一个假动画**
 *
 * 不搞"静态字形 + 闪烁"糊过去：`not_in_library` 就说"这个字暂时没有笔顺数据"，
 * 网络问题就提示可以再点一次。田字格与拼音**照常显示**（它们不依赖任何数据），
 * 所以即使笔顺这一块挂了，预习卡本身仍然有用。
 */
import HanziWriter from 'hanzi-writer';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../ui/Button';
import { IconAlertTriangle, IconReplay } from '../ui/Icons';
import { TianZiGeFrame } from './TianZiGe';
import { pinyinLabel } from '../../lib/dictationPreview';
import {
  describeStrokeDataFailure,
  loadStrokeData,
  type StrokeDataResult,
} from '../../lib/hanziStrokes';

/**
 * 画布边长（px）。
 *
 * ⚠️ Hanzi Writer 必须拿到**明确的像素尺寸**（它按这个尺寸算缩放与 SVG 视图盒），
 *   所以这里是个常量，而不是"跟着 CSS 走"。
 *
 * 取 168 而不是格子的 176：格子是 `h-44 w-44`（=176px）**含边框**，选中态还有
 * 2px 的 `border-2`，内容区只有 172px。留 4px 余量之后，Hanzi Writer 画出来的
 * 字形（它自己还有 `padding: 8`）**永远不会被格子的 `overflow-hidden` 切掉边**。
 */
const WRITER_SIZE = 168;

/** 播一遍笔顺。动画被取消/渲染失败**不抛异常**：田字格本身还在，不该打扰家长。 */
async function animateOnce(writer: HanziWriter): Promise<void> {
  try {
    const pending = await writer.animateCharacter();
    await pending;
  } catch {
    /* 刻意吞掉：这不是家长需要知道的事，也不影响字形本身 */
  }
}

type Failure = Extract<StrokeDataResult, { ok: false }>;

export function StrokeOrderCard({
  char,
  pinyin,
  onClose,
}: {
  char: string;
  /** 这个字的读音（`null` = 拿不到，显示「—」）。 */
  pinyin: string | null;
  onClose: () => void;
}) {
  const holderRef = useRef<HTMLSpanElement | null>(null);
  const writerRef = useRef<HanziWriter | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [failure, setFailure] = useState<Failure | null>(null);
  const [strokeCount, setStrokeCount] = useState<number | null>(null);
  /** 点"再试一次"时 +1，让下面的 effect 重跑（比直接调函数更不容易漏清理）。 */
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    const holder = holderRef.current;
    writerRef.current = null;
    setStatus('loading');
    setFailure(null);
    setStrokeCount(null);
    if (holder === null) return;

    let cancelled = false;
    holder.innerHTML = '';

    void loadStrokeData(char).then((result) => {
      if (cancelled || holderRef.current !== holder) return;
      if (!result.ok) {
        setFailure(result);
        setStatus('failed');
        return;
      }
      const writer = HanziWriter.create(holder, char, {
        width: WRITER_SIZE,
        height: WRITER_SIZE,
        padding: 8,
        // 淡淡的字形轮廓：预习时先看清"这个字长什么样"，再看笔顺
        showOutline: true,
        // SVG 的 stroke 属性不走 Tailwind，所以这几个颜色只能写在这里（slate 系）
        strokeColor: '#0f172a',
        outlineColor: '#e2e8f0',
        strokeAnimationSpeed: 1,
        delayBetweenStrokes: 220,
        /**
         * 数据刚在上一行取回来了，这里**直接把同一份数据交回去** ——
         * 不发第二次请求，也不可能出现"取了一次成功、画的时候又失败"。
         */
        charDataLoader: (_targetChar, onLoad) => {
          onLoad(result.data);
        },
      });
      writerRef.current = writer;
      setStrokeCount(result.data.strokes.length);
      setStatus('ready');
      void animateOnce(writer);
    });

    return () => {
      cancelled = true;
      const writer = writerRef.current;
      writerRef.current = null;
      if (writer !== null) void writer.pauseAnimation().catch(() => undefined);
      holder.innerHTML = '';
    };
  }, [char, retryToken]);

  const replay = useCallback(() => {
    const writer = writerRef.current;
    if (writer === null) return;
    void animateOnce(writer);
  }, []);

  return (
    <div className="mt-3 rounded-2xl border border-brand-200 bg-brand-50/50 p-3">
      <div className="flex flex-wrap items-start gap-3">
        {/* 格子这一列**不参与收缩**（窄屏约定：让文字列自己让位） */}
        <TianZiGeFrame size="lg" selected className="relative">
          {/* 画布居中（格子内容区比画布大 4px），并且让 loading 层的定位基准落在格子上 */}
          <span className="flex h-full w-full items-center justify-center">
            <span
              ref={holderRef}
              role="img"
              aria-label={`「${char}」的笔顺动画`}
              aria-busy={status === 'loading'}
              style={{ width: WRITER_SIZE, height: WRITER_SIZE }}
              className="block"
            />
          </span>
          {status === 'loading' && (
            <span className="absolute inset-0 z-20 flex items-center justify-center bg-white/70 text-xs text-slate-500">
              正在取这个字的笔顺…
            </span>
          )}
        </TianZiGeFrame>

        <div className="min-w-0 flex-1 space-y-2">
          <p className="text-sm font-semibold text-slate-900">
            「{char}」的笔顺
            <span className="ml-2 font-mono text-xs font-normal text-slate-500">
              {pinyinLabel(pinyin)}
            </span>
          </p>

          {status === 'ready' && (
            <p className="text-xs text-slate-500">
              {strokeCount === null ? '—' : `共 ${strokeCount} 笔`}
              ：看它一笔一笔写出来，跟着在纸上写一遍。
            </p>
          )}

          {status === 'failed' && failure !== null && (
            <p className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <span className="mt-0.5 shrink-0">
                <IconAlertTriangle size={14} />
              </span>
              <span>
                {describeStrokeDataFailure(failure.reason)}
                <span className="mt-0.5 block text-[11px] text-amber-700/80">
                  （{failure.detail}）
                </span>
              </span>
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {status === 'ready' && (
              <Button size="sm" variant="secondary" icon={<IconReplay size={16} />} onClick={replay}>
                再放一遍
              </Button>
            )}
            {status === 'failed' && (failure?.reason === 'network' || failure?.reason === 'bad_payload') && (
              <Button size="sm" variant="secondary" onClick={() => setRetryToken((n) => n + 1)}>
                再试一次
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={onClose}>
              收起
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
